import { Dialog, TextField } from '@material-ui/core'
import { Button } from 'components/facets/Button'
import { useUser } from 'components/providers/UserProvider'
import {
  buildPanelDeltas,
  ChangesContentInterface,
  ChangesInterface,
  useLocationStore,
} from 'lib/hooks/useLocationStore'
import { wouldGoBelowReserved } from 'lib/reservedQuantity'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CardExtraAttribute,
  Game,
  InventoryArticle,
  isSingle,
  MkmProductInventoryMap,
} from 'shared'
import {
  EXTRA_ATTRIBUTES_VALUES_AND_IMAGES_MAP,
  LOCATIONS_PANEL,
  PANEL_LOCATION_PLACEHOLDER,
} from 'utils/constants'
import { VisualArticle } from '../StockDisplay/VisualArticle'
import { LocationsPanelNewRow } from './LocationPanelNewRow'
import './locations-panel.css'
import { LocationsPanelRow } from './LocationsPanelRow'

/* for each row it reads the initial values of article.locations and populates each row with an identifying rowIndex;
currentValues is the list of rows that are modified, and is modified via useEffect.
specifically in both LocationPanelRow and LocationPanelNewRow the useEffect is activated if an attribute undergoes changes;
if a value has changed, it inserts the entire corresponding row into currentValues.
Subsequently in LocationPanel another useEffect is activated when currentValues is modified, then calls the getChanges function 
which compares the row present in the initial values with the one present in the currentValues. 
If one or more attributes in this row have changed, they are placed in changes.
If the row does not exist in initialValues then it is a new row added.
To add new rows, the list of attributes are based on the template saved in local storage. The object with this collection of attributes 
is inserted both in newRows (to render the new component which has a different behavior), both in currentValues 
as it is actually a different value because it is new. In this case the entire line is inserted into changes.
For the same reason, when a row is deleted, both structures are operated 

rowId is an object used to distinguish cards that have the same location but are different rows.
The rowId state is used to create new rows; these have a different structure index to differentiate them from the already existing initial lines;
the rowId of existing rows is instead implemented when looping through the LocationPanelRow component. It follows the same structure as initialValues to be able to check the changes made.
In any case, when the user confirms the changes he wants to make, within InventoryPresenter the handleConfirmLocationsPanel function will read each row and evaluate how to modify the stock in relation to its attributes*/

export interface LocationsPanelProps {
  open: boolean
  activeGame: Game
  article: InventoryArticle
  inventoryMap: MkmProductInventoryMap
  handleConfirm: (changes: any) => void
  handleClose: () => void
}

export const LocationsPanel = ({
  open,
  activeGame,
  article,
  inventoryMap,
  handleConfirm,
  handleClose,
}: LocationsPanelProps): JSX.Element => {
  const { user } = useUser()
  const { currentValues, setCurrentValues, setResetLocationRows, newRows, setNewRows } =
    useLocationStore((state) => state)

  const [rowId, setRowId] = useState(0)

  const gameInformations = EXTRA_ATTRIBUTES_VALUES_AND_IMAGES_MAP.find(
    (game) => game.idGame === activeGame.idGame
  )!

  const articleComment = article.newComment ?? article.comment

  const [changes, setChanges] = useState<any>({})

  const getInitialValues = () => {
    const initialState: ChangesInterface = {}
    article.locations?.forEach((location: any, index: number) => {
      index += 1
      initialState[index + PANEL_LOCATION_PLACEHOLDER + location.name] = {
        quantity: location.quantity,
        deltaQuantity: 0,
        locationName: location.name,
        condition: article.newCondition || article.condition,
        idLanguage: article.newIdLanguage || article.idLanguage,
        isSigned: article.isSigned,
        finishType: article.finishType,
        countryEdition: article.countryEdition,
        isFirstEd: article.isFirstEd,
        isNewRow: undefined,
        rowId: index + PANEL_LOCATION_PLACEHOLDER + location.name,
        gradingCompany: article.graded.gradingCompany,
        grade: article.graded.grade,
      }
    })
    return initialState
  }

  const initialValues = getInitialValues()
  const belowReservedQuantity = wouldGoBelowReserved(
    inventoryMap,
    article,
    buildPanelDeltas(currentValues, initialValues)
  )
  const violationReserved = Number(article.ptArticleReservedQuantity ?? 0)
  const single = isSingle(article.card)
  const { t } = useTranslation()
  const finishTypes = gameInformations.finishType
    .filter((ft) => ft[single ? 'singles' : 'sealed'])
    .map((ft) => ft.label)

  const handleReset = () => {
    setResetLocationRows(true)
    setCurrentValues(initialValues)
    setChanges({})
    setNewRows([])
  }

  const handleNewCard = () => {
    setRowId(rowId + 1)

    const newLocation: ChangesContentInterface = {
      quantity: 0,
      idLanguage: article.idLanguage,
      condition: article.condition,
      finishType: article.finishType,
      isSigned: article.isSigned,
      isFirstEd: article.isFirstEd,
      countryEdition: article.countryEdition,
      locationName: '',
      deltaQuantity: 1,
      isNewRow: true,
      rowId: rowId + '-' + rowId + PANEL_LOCATION_PLACEHOLDER,
      gradingCompany: undefined,
      grade: undefined,
    }

    setNewRows([...newRows, newLocation])

    const newCurrentValues: ChangesInterface = { ...currentValues }
    newCurrentValues[rowId + '-' + rowId + PANEL_LOCATION_PLACEHOLDER] = newLocation

    setCurrentValues(newCurrentValues)
  }

  const deleteNewRow = (rowId: string) => {
    let rowToRemove: any
    if (currentValues.hasOwnProperty(rowId)) {
      rowToRemove = currentValues[rowId]
    }
    ///update currentvalues
    const updatedCurrentValues = { ...currentValues }
    Object.keys(currentValues).forEach((key) => {
      if (key === rowId) {
        delete updatedCurrentValues[key]
      }
    })
    setCurrentValues(updatedCurrentValues)

    //update newRows
    const copyOfNewRows = newRows.filter((row: any) => row.rowId !== rowToRemove.rowId)

    setNewRows(copyOfNewRows)
  }

  useEffect(() => {
    let newChanges: ChangesInterface = {}

    Object.keys(currentValues).forEach((key) => {
      const initialValue = initialValues[key]
      const currentValue = currentValues[key]
      newChanges = getChanges(key, initialValue, currentValue, newChanges)
    })

    setChanges(newChanges)
  }, [currentValues])

  const getChanges = (
    locationName: string,
    initialValue: any,
    currentValue: any,
    newChanges: any
  ) => {
    const updatedChanges = { ...newChanges }

    if (currentValue && initialValue) {
      Object.keys(currentValue).forEach((attribute) => {
        const currentAttributeValue = currentValue[attribute]

        // const parts = locationName.split('#')
        // const realLocationName = parts.slice(1).join('#')

        if (attribute !== 'quantity' && currentAttributeValue !== initialValue[attribute]) {
          updatedChanges[locationName] = {
            ...updatedChanges[locationName],
            quantity: currentValue.quantity,
            locationName: currentValue.locationName,
            [attribute]: currentAttributeValue,
          }
        } else {
          if (updatedChanges[locationName]) {
            delete updatedChanges[locationName][attribute]
            if (Object.keys(updatedChanges[locationName]).length === 0) {
              delete updatedChanges[locationName]
            }
          }
        }
      })
    } else if (!initialValue) {
      Object.keys(currentValue).forEach((attribute) => {
        const currentAttributeValue = currentValue[attribute]
        updatedChanges[locationName] = {
          ...updatedChanges[locationName],
          quantity: currentValue.quantity,
          [attribute]: currentAttributeValue,
        }
      })
    } else {
      console.log(`currentValue or initialValue for ${locationName} not found.`)
    }

    return updatedChanges
  }

  return (
    <Dialog onClose={handleClose} open={open} maxWidth="xl">
      <div className="p-4">
        <div
          className={` ${
            articleComment === '' ? `d-none` : `ms-2 d-flex flex-row align-items-end`
          }`}
        >
          <i className="fa-light fa-message-dots location-panel-comment-icon me-3"></i>

          <TextField
            label={t('common.comment')}
            defaultValue={articleComment}
            InputProps={{
              readOnly: true,
            }}
            variant="standard"
            disabled={true}
            className="location-panel-comment"
          />
        </div>
        <div className="d-flex flex-column flex-xl-row align-items-start mt-3">
          <div className="mb-3 mb-lg-0">
            <VisualArticle
              article={article}
              visualArticleSizeClass={'one'}
              displayOnly={true}
              parentEl={LOCATIONS_PANEL}
              currency={user.currency}
            />
          </div>
          <div className="mt-3 location-panel-table-wrapper">
            <table className="location-panel-table">
              <thead>
                <tr>
                  <td align="center">
                    <h6 className="m-0 p-0">{t('locationPanel.quantityAbbreviation')}</h6>
                  </td>
                  <td align="center">
                    <h6 className="m-0 p-0">{t('locationPanel.changeQuantity')}</h6>
                  </td>
                  <td align="center">
                    <h6 className="m-0 p-0">{t('common.edit')}</h6>
                  </td>
                  <td align="center">
                    <h6 className="m-0 p-0">{t('common.language')}</h6>
                  </td>
                  {single ? (
                    <td align="center">
                      <h6 className="m-0 p-0">{t('common.condition')}</h6>
                    </td>
                  ) : (
                    <></>
                  )}
                  {finishTypes.length > 0 ? (
                    <td align="center">
                      <h6 className="m-0 p-0">{t('stockPricing.finishType')}</h6>
                    </td>
                  ) : null}

                  {single && gameInformations.countryEdition.length > 0 ? (
                    <td align="center">
                      <h6 className="m-0 p-0">{t('stockPricing.countryEdition')}</h6>
                    </td>
                  ) : null}

                  {single &&
                  gameInformations.cardExtraAttribute.some(
                    (element) => element.attribute === CardExtraAttribute.Signed
                  ) ? (
                    <td align="center">
                      <h6 className="m-0 p-0">{t('common.isSigned')}</h6>
                    </td>
                  ) : null}

                  {gameInformations.cardExtraAttribute.some(
                    (element) => element.attribute === CardExtraAttribute.FirstEd
                  ) ? (
                    <td align="center">
                      <h6 className="m-0 p-0">{t('common.isFirstEd')}</h6>
                    </td>
                  ) : null}

                  {article.graded.isGraded && (
                    <td align="center">
                      <h6 className="m-0 p-0">{t('stockPricing.grading')}</h6>
                    </td>
                  )}

                  <td align="center">
                    <h6 className="m-0 p-0">{t('common.location')}</h6>
                  </td>
                  <td align="center">
                    <h6 className="m-0 p-0">{t('common.actions')}</h6>
                  </td>
                </tr>
              </thead>
              <tbody>
                {article.locations?.map((location: any, index: number) => (
                  <LocationsPanelRow
                    key={index + 1 + PANEL_LOCATION_PLACEHOLDER}
                    article={article}
                    location={location}
                    rowId={index + 1 + PANEL_LOCATION_PLACEHOLDER}
                    data-testid={index + 1 + PANEL_LOCATION_PLACEHOLDER}
                    gameInformations={gameInformations}
                    isSingle={single!}
                  />
                ))}
                {newRows.map((element: any) => {
                  return (
                    <LocationsPanelNewRow
                      key={element.rowId}
                      article={article}
                      location={element}
                      rowId={element.rowId}
                      deleteNewRow={deleteNewRow}
                      data-testid={element.rowId}
                      gameInformations={gameInformations}
                      isSingle={single!}
                    />
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
        {belowReservedQuantity && (
          <div className="text-danger mt-3" data-testid="below-reserved-quantity-warning">
            {t('locationPanel.belowReservedQuantity', { reserved: violationReserved })}
          </div>
        )}
        <div className="d-flex flex-wrap justify-content-end gap-2 mb-3 mt-5 my-lg-3">
          <Button variant="outlined" onClick={handleClose} data-testid="close-panel">
            {t('common.cancel')}
          </Button>
          <Button
            variant="outlined"
            onClick={handleNewCard}
            data-testid="add-new-card-location-btn"
            disabled={article.graded.isGraded}
          >
            {t('locationPanel.addNewCard')}
          </Button>
          <Button
            variant="outlined"
            data-testid="reset-all-changes-btn"
            disabled={Object.keys(changes).length === 0}
            onClick={handleReset}
          >
            {t('locationPanel.resetAll')}
          </Button>
          <Button
            data-testid="confirm-changes-location-btn"
            variant="contained"
            disabled={Object.keys(changes).length === 0 || belowReservedQuantity}
            onClick={() => handleConfirm(changes)}
          >
            {t('common.confirm')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
